// ============================================================================
// Qué hace el interceptor de la API con los tres 403 de seguridad.
// ============================================================================
//   STEP_UP_REQUIRED                 abre el diálogo del código y, si lo da,
//   TWO_FACTOR_CHALLENGE_REQUIRED    REPITE la petición original.
//   TWO_FACTOR_ENROLLMENT_REQUIRED   la cuenta está obligada a activar 2FA: se
//                                    avisa para llevarla a su perfil. No hay
//                                    nada que repetir.
//
// Ninguno es «sesión inválida»: la sesión está viva, sólo le falta un paso. Por
// eso se resuelven ANTES de la rama genérica de 403, que podría cerrar sesión.
// ============================================================================

import { apiErrorCode } from "../utils/apiErrorCodes";
import { requestSecurityChallenge } from "./securityChallengeStore";

export const SECURITY_ENROLLMENT_EVENT = "security:enrollment-required";
/** Alta manual sin correo confirmado (CC-CONSOLE#1550): a /verificar. */
export const SIGNUP_VERIFICATION_EVENT = "security:signup-verification-required";

const STEP_UP_REQUIRED = "STEP_UP_REQUIRED";
const CHALLENGE_REQUIRED = "TWO_FACTOR_CHALLENGE_REQUIRED";
const ENROLLMENT_REQUIRED = "TWO_FACTOR_ENROLLMENT_REQUIRED";
const EMAIL_VERIFICATION_REQUIRED = "EMAIL_VERIFICATION_REQUIRED";

type RetriableConfig = { url?: string; _securityRetry?: boolean };
type SecurityError = {
  response?: { status?: number; data?: { meta?: { operation?: string; label?: { es?: string; en?: string } } } };
  config?: RetriableConfig;
};

/** ¿Es uno de los 403 de seguridad? (para que nadie más lo trate como sesión caída). */
export const isSecurityGateError = (error: unknown): boolean => {
  if ((error as SecurityError)?.response?.status !== 403) return false;
  const code = apiErrorCode(error);
  return (
    code === STEP_UP_REQUIRED ||
    code === CHALLENGE_REQUIRED ||
    code === ENROLLMENT_REQUIRED ||
    code === EMAIL_VERIFICATION_REQUIRED
  );
};

/**
 * Resuelve un 403 de seguridad.
 *   • `{ retried: true, response }`  → se dio el código y la petición se repitió;
 *   • `{ retried: false }`           → era de seguridad pero no se repite (lo
 *                                      canceló, ya se había reintentado, o es la
 *                                      inscripción obligatoria): se rechaza;
 *   • `null`                         → no era un 403 de seguridad.
 */
export async function resolveSecurityError<T>(
  error: unknown,
  retry: (config: RetriableConfig) => Promise<T>,
): Promise<{ retried: true; response: T } | { retried: false } | null> {
  if (!isSecurityGateError(error)) return null;
  const code = apiErrorCode(error);
  const config = (error as SecurityError).config;

  if (code === ENROLLMENT_REQUIRED) {
    window.dispatchEvent(new CustomEvent(SECURITY_ENROLLMENT_EVENT));
    return { retried: false };
  }
  // Alta manual que aún no confirma su correo: no es sesión caída ni hay nada
  // que repetir; la pantalla la lleva a /verificar.
  if (code === EMAIL_VERIFICATION_REQUIRED) {
    window.dispatchEvent(new CustomEvent(SIGNUP_VERIFICATION_EVENT));
    return { retried: false };
  }

  // El propio reto nunca se «reintenta con otro reto», y una petición sólo se
  // repite una vez: si tras dar el código vuelve a pedirlo, algo anda mal y es
  // mejor enseñar el error que entrar en un ciclo de diálogos.
  if (!config || config._securityRetry || config.url?.includes("/security/challenge/")) {
    return { retried: false };
  }

  const ok = await requestSecurityChallenge({
    purpose: code === CHALLENGE_REQUIRED ? "login" : "step_up",
    operation: (error as SecurityError).response?.data?.meta?.operation,
    label: (error as SecurityError).response?.data?.meta?.label,
  });
  if (!ok) return { retried: false };

  config._securityRetry = true;
  return { retried: true, response: await retry(config) };
}
